import {isFunction, isObject} from 'es-toolkit/compat'

import {loadScript, waitForApi} from './critical'

const RETRY_LIMIT = 5
const RETRY_INTERVAL = 1000
const ATTACHMENT_LIMIT = 5
const MAX_REQUEST_SIZE = 10000000

let dixaInitialized = false
let dixaWidgetElement: Element
let dixaResizeObserver: ResizeObserver

const isDixaBlocked = () => !window._dixa

const dixa = (
  cmd: string,
  options: string | Record<string, unknown>,
  moreOptionsOrCallback?: string | ((...args: never[]) => void)
): void => {
  if (window._dixa) {
    window._dixa(
      cmd,
      isObject(options) && !isFunction(options) ? JSON.stringify(options) : options,
      moreOptionsOrCallback
    )
  }
}

const initResizeObserver = () => {
  if (window.ResizeObserver) {
    dixaResizeObserver = new ResizeObserver(() => {
      if (dixaWidgetElement.clientHeight < 150) {
        window.document.body.classList.remove('u-dixa-visible')
        dixaResizeObserver.disconnect()
      }
    })
  }
}

export const canOpenDixa = () => dixaInitialized && !!dixaWidgetElement && !isDixaBlocked()

export const openDixa = () => {
  dixa('api.setView', 'conversation')

  setTimeout(() => {
    // dixa's "api.setView" takes some time. If we set dixa visible to early,
    // the user would see the minimzed widget shortly before the conversation is visible.
    window.document.body.classList.add('u-dixa-visible')
    if (dixaResizeObserver) {
      dixaResizeObserver.observe(dixaWidgetElement)
    }
  }, 300)
}

export const closeDixa = () => {
  dixa('api.setView', 'hidden')
  window.document.body.classList.remove('u-dixa-visible')
}

export const setUser = (name: string, email: string) => {
  dixa('api.setUser', name, email)
}

export const init = (enabled: boolean, setDefaultUser: boolean) => {
  if (enabled) {
    loadScript(window.CRAFTCLOUD_DIXA_SRC)
    waitForApi(
      () => !!window._dixa,
      () => {
        dixaInitialized = true

        let retries = 0

        // It takes some time until the widget element is fully loaded and accessible
        const timer = setInterval(() => {
          const dixaWidgetWrapper = document.getElementById('dixa-widget-wrapper')
          if (dixaWidgetWrapper) {
            dixaWidgetElement = dixaWidgetWrapper
            initResizeObserver()

            if (setDefaultUser) {
              // The solution to prevent the form inputs was provided by dixa
              // https://github.com/all3dp/craftcloud-client/issues/2127
              dixa('api.setUser', 'Your name', 'youremail@example.com')
            }

            dixa('api.subscribe', 'onMessageAdded', () => openDixa())

            clearInterval(timer)
          }
          if (++retries === RETRY_LIMIT) {
            clearInterval(timer)
          }
        }, RETRY_INTERVAL)
      }
    )
  } else {
    // eslint-disable-next-line no-console
    console.info('*** Dixa disabled ***')
  }
}

type ContactFormData = {
  name: string
  email: string
  message: string
  sendCopy: boolean
  [key: string]: string | boolean
}

type ContactSalesFormData = {
  appId: string
  name: string
  email: string
  message: string
  quoteDue?: string
  deliveryDue?: string
  maxBudget?: string
  company: string
  location: string
  sendCopy: boolean
  subject: string
  attachments: File[]
  [key: string]: string | boolean | File[] | undefined
}

type RequestHelpData = {
  name: string
  email: string
  orderNumber: string
  vendorId: string
  message: string
  [key: string]: string
}

export type FeedbackFormData = {
  name: string
  email: string
  message: string
  sendCopy: boolean
  orderId?: string
  vendorId?: string
  [key: string]: string | boolean | undefined
}

export const submitForm = (payload: object, endpoint: string) => {
  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(v => formData.append(key, v))
    } else {
      formData.append(key, value)
    }
  })

  return fetch(endpoint, {
    method: 'POST',
    body: formData,
  }).then(response => response.json())
}

export const submitContactForm = (endpoint: string, data: ContactFormData) =>
  submitForm(data, endpoint)

type PartnerFormData = ContactFormData & {
  company: string
  website?: string
}

export const submitPartnerForm = (endpoint: string, data: PartnerFormData) =>
  submitForm(data, endpoint)

export const submitRequestHelpForm = (endpoint: string, data: RequestHelpData) =>
  submitForm(data, endpoint)

const packAttachments = (
  attachments: File[],
  maxFilesPerBin = ATTACHMENT_LIMIT,
  maxFileSizePerBin = MAX_REQUEST_SIZE
) => {
  const sortedAttachments = attachments.sort((a, b) => b.size - a.size)
  const bins: {
    files: File[]
    size: number
  }[] = [
    {
      files: [],
      size: 0,
    },
  ]

  sortedAttachments.forEach(attachment => {
    const noSpaceLeft = bins.every(bin => {
      if (bin.size + attachment.size <= maxFileSizePerBin && bin.files.length < maxFilesPerBin) {
        bin.files.push(attachment)
        bin.size += attachment.size
        return false
      }
      return true
    })

    if (noSpaceLeft) {
      bins.push({
        files: [attachment],
        size: attachment.size,
      })
    }
  })

  return bins.map(bin => bin.files)
}

export const submitContactSalesForm = (endpoint: string, data: ContactSalesFormData) => {
  const attachmentChunks = packAttachments(data.attachments)
  return Promise.all(
    attachmentChunks.map(attachments =>
      submitForm(
        {
          ...data,
          attachments,
        },
        endpoint
      )
    )
  ).then(responses => {
    const errorResponse = responses.find(response => !!response.error_description)
    if (errorResponse) {
      throw errorResponse
    }

    return responses
  })
}

export const submitFeedbackForm = (endpoint: string, data: FeedbackFormData) =>
  submitForm(data, endpoint)
